import { Breadcrumb, Button, Col, Row, Space, Table, Tooltip} from 'antd';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { font } from '../../Common/font';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as PrintIcon } from '../../Images/PrintIcon.svg';
import {
  fetchCurrentStockByCompanyId,
  fetchCurrentStockByCompanyIdDetails,
} from '../../slices/current-stock';
import * as XLSX from 'xlsx';
import { FileExcelOutlined } from '@ant-design/icons';

function CurrentStockList() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { dataPartyWiseSummary = [], status } = useSelector(state => state.currentStock);
  const { dataTwo = []} = useSelector(state => state.currentStock);
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(5000);

  const dataSource = dataPartyWiseSummary.map((item, index) => ({
    ...item,
    key: item.id ?? `page-${page}-row-${index}`,
    SrNo: (page - 1) * pagesize + index + 1,
  }));

  const dataSourceTwo = dataTwo.map((item, index) => ({
    ...item,
    key: item.id ?? `page-${page}-row-${index}`,
    SrNo: (page - 1) * pagesize + index + 1,
  }));

  useEffect(() => {
    dispatch(
      fetchCurrentStockByCompanyIdDetails({
        page,
        pagesize,
        id
      }),
    );
  }, [dispatch, page, pagesize, id]);

  useEffect(() => {
    const fetchData = async () => {
     await dispatch(
        fetchCurrentStockByCompanyId({
          page,
          pagesize,
          id
        }),
      );
    };

    fetchData();
  }, [dispatch, page, pagesize, id]);

  const [checkedList] = useState([
    'SrNo',
    'workorder_no',
    'bundle_no',
    'section',
    'length_mm',
    'item_name',
    'pieces',
    'weight_kg',
    'avg_weight_kg',
    'packing_date',
    'customer',
    'action',
  ]);

  const columns = [
     {
      title: 'Work Order No',
      dataIndex: 'workorder_no',
      key: 'workorder_no',
    },
    {
      title: 'Bundle No',
      dataIndex: 'bundle_no',
      key: 'bundle_no',
    },
    {
      title: 'Profile No',
      dataIndex: 'section',
      key: 'section',
    },
    {
      title: 'Length(mm)',
      dataIndex: 'length_mm',
      key: 'length_mm',
    },
    {
      title: 'Profile Description',
      dataIndex: 'item_name',
      key: 'item_name',
    },
    {
      title: 'Pieces',
      dataIndex: 'pieces',
      key: 'pieces',
    },
    {
      title: 'Weight(kg)',
      dataIndex: 'weight_kg',
      key: 'weight_kg',
      render: value => value?.toFixed(3),
    },
    {
      title: 'Avg Wt(kg)',
      dataIndex: 'avg_weight_kg',
      key: 'avg_weight_kg',
      render: value => value?.toFixed(3),
    },
    {
      title: 'Packing Date',
      dataIndex: 'packing_date',
      key: 'packing_date',
      render: packing_date => dayjs(packing_date).format('DD-MM-YYYY hh:mm'),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: record => {
        return (
          <span>
            {record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      render: record => {
        return (
          <span>
            {record ? `${record.first_name} ${record.last_name}` : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: record => {
        return (
          <span>
            {record && record.trim()
              ? dayjs(record).format('DD-MM-YYYY HH:mm')
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      render: record => {
        return (
          <span>
            {record && record.first_name?.trim() && record.last_name?.trim()
              ? `${record.first_name} ${record.last_name}`
              : 'N/A'}
          </span>
        );
      },
    },
  ];

  const transformData = dataPartyWiseSummary => {
    const flattenedData = [];

    dataPartyWiseSummary?.forEach((order, orderIndex) => {
      const detailLength = order.details?.length || 0;

      if (!detailLength) {
        flattenedData.push({
          key: `${orderIndex}`,
          work_order_no: order.work_order_no,
          section: order.section || '',
          die_diagram: order.die_diagram || '',
          length_mm: order.length_mm || '',
          item_name: order.item_name || '',
          order_kg: order.order_kg || 0,
          order_pcs: order.order_pcs || 0,
          packed_wt_kg: order.packed_wt_kg || 0,
          packed_pcs: order.packed_pcs || 0,
        });
      } else {
        order.details.forEach((detail, detailIndex) => {
          flattenedData.push({
            key: `${orderIndex}-${detailIndex}`,
            work_order_no: order.work_order_no,
            section: detail.section || '',
            die_diagram: order.die_diagram || '',
            length_mm: detail.length_mm,
            item_name: detail.item_name,
            order_kg: detail.order_kg,
            order_pcs: detail.order_pcs,
            packed_wt_kg: detail.packed_wt_kg,
            packed_pcs: detail.packed_pcs,
          });
        });
      }
    });
    const workOrderGroups = {};
    flattenedData.forEach(row => {
      if (!workOrderGroups[row.work_order_no]) {
        workOrderGroups[row.work_order_no] = [];
      }
      workOrderGroups[row.work_order_no].push(row);
    });
    const transformedData = [];

    Object.keys(workOrderGroups).forEach(workOrder => {
      const workOrderRows = workOrderGroups[workOrder];
      workOrderRows.sort((a, b) => a.section - b.section);
      const sectionGroups = {};
      workOrderRows.forEach(row => {
        if (!sectionGroups[row.section]) {
          sectionGroups[row.section] = [];
        }
        sectionGroups[row.section].push(row);
      });
      const sectionKeys = Object.keys(sectionGroups).sort((a, b) => a - b);
      const workOrderSpan = workOrderRows.length;

      sectionKeys.forEach((section, sectionIndex) => {
        const sectionRows = sectionGroups[section];
        const sectionSpan = sectionRows.length;

        sectionRows.forEach((row, rowIndex) => {
          transformedData.push({
            ...row,
            rowSpan: 1,
            workOrderSpan:
              sectionIndex === 0 && rowIndex === 0 ? workOrderSpan : 0,
            sectionSpan: rowIndex === 0 ? sectionSpan : 0,
          });
        });
      });
    });

    return transformedData;
  };
  const transformedData = transformData(dataPartyWiseSummary);
  const summaryColumns = [
    {
      title: 'Work Order No',
      dataIndex: 'work_order_no',
      render: (text, row) => ({
        children: row.workOrderSpan === 0 ? null : text,
        props: {
          rowSpan: row.workOrderSpan,
        },
      }),
      align: 'center',
    },
    {
      title: 'Profile No',
      dataIndex: 'section',
      render: (text, row) => ({
        children: row.sectionSpan === 0 ? null : text,
        props: {
          rowSpan: row.sectionSpan,
        },
      }),
    },
    {
      title: 'Profile Image',
      dataIndex: 'image',
      key: 'image',
      width: 100,

      render: (text, record) => {
        if (record.die_diagram) {
          return (
            <img
              src={record.die_diagram}
              alt="Section Diagram"
              style={{
                maxWidth: '90px',
                maxHeight: '90px',
                objectFit: 'contain',
              }}
            />
          );
        }
        return '-';
      },
      align: 'center',
    },
    {
      title: 'Length (mm)',
      dataIndex: 'length_mm',
      key: 'length_mm',
    },
    {
      title: 'Profile Description',
      dataIndex: 'item_name',
      key: 'item_name',
    },
    {
      title: 'Order (Kg)',
      dataIndex: 'order_kg',
      key: 'order_kg',
      render: value => value?.toFixed(3),
    },
    {
      title: 'Order (Pcs)',
      dataIndex: 'order_pcs',
      key: 'order_pcs',
    },
    {
      title: 'Packed Wt (Kg)',
      dataIndex: 'packed_wt_kg',
      key: 'packed_wt_kg',
      render: value => value?.toFixed(3),
    },
    {
      title: 'Packed (Pcs)',
      dataIndex: 'packed_pcs',
      key: 'packed_pcs',
    },
  ];

  const filteredColumns = columns.filter(column =>
    checkedList.includes(column.key),
  );

  const calculateTotalRow = data => {
    let totalOrderKg = 0,
      totalOrderPcs = 0,
      totalPackedWt = 0,
      totalPackedPcs = 0;

    data.forEach(({ order_kg, order_pcs, packed_wt_kg, packed_pcs }) => {
      totalOrderKg += order_kg;
      totalOrderPcs += order_pcs;
      totalPackedWt += packed_wt_kg;
      totalPackedPcs += packed_pcs;
    });
    return {
      key: 'total',
      work_order_number: '',
      section: '',
      length_mm: '',
      item_name: 'Total',
      order_kg: totalOrderKg?.toFixed(3),
      order_pcs: totalOrderPcs,
      packed_wt_kg: totalPackedWt?.toFixed(3),
      packed_pcs: totalPackedPcs,
    };
  };


  const handlePrintDownload = async () => {
    const pdf = new jsPDF('pt', 'pt', 'a4');
    pdf.addFileToVFS('WorkSans-normal.ttf', font);
    pdf.addFont('WorkSans-normal.ttf', 'WorkSans', 'normal');
    pdf.setFont('WorkSans');
    const mergeCellTracker = {
      workOrderGroups: {},
      sectionGroups: {},
    };
    const preprocessForMerge = data => {
      if (!data || !Array.isArray(data)) return [];
      const workOrderGroups = {};
      data.forEach(item => {
        const workOrderNo = item.work_order_no || 'N/A';
        if (!workOrderGroups[workOrderNo]) {
          workOrderGroups[workOrderNo] = [];
        }
        workOrderGroups[workOrderNo].push(item);
      });
      const result = [];
      let currentRowIndex = 0;

      Object.keys(workOrderGroups).forEach(workOrderNo => {
        const workOrderItems = workOrderGroups[workOrderNo];
        workOrderItems.sort((a, b) => {
          const sectionA = isNaN(a.section) ? 0 : Number(a.section);
          const sectionB = isNaN(b.section) ? 0 : Number(b.section);
          return sectionA - sectionB;
        });
        const sectionGroups = {};
        workOrderItems.forEach(item => {
          const section = item.section || 'N/A';
          if (!sectionGroups[section]) {
            sectionGroups[section] = [];
          }
          sectionGroups[section].push(item);
        });
        const workOrderStartRow = currentRowIndex;
        let totalWorkOrderRows = 0;
        Object.keys(sectionGroups).forEach(section => {
          const sectionItems = sectionGroups[section];
          const sectionStartRow = currentRowIndex;
          const sectionRowCount = sectionItems.length;
          totalWorkOrderRows += sectionRowCount;
          sectionItems.forEach(item => {
            result.push(item);
            currentRowIndex++;
          });
          mergeCellTracker.sectionGroups[sectionStartRow] = {
            rowSpan: sectionRowCount,
            section: section,
          };
        });
        mergeCellTracker.workOrderGroups[workOrderStartRow] = {
          rowSpan: totalWorkOrderRows,
          workOrderNo: workOrderNo,
        };
      });

      return result;
    };
    const transformSummaryData = data => {
      if (!data || !Array.isArray(data)) return [];

      const flattenedData = [];
      data.forEach((order, orderIndex) => {
        const detailLength = order.details?.length || 0;
        if (!detailLength) {
          flattenedData.push({
            key: `${orderIndex}`,
            work_order_no: order.work_order_no,
            section: order.section || '',
            length_mm: order.length_mm || '',
            item_name: order.item_name || '',
            order_kg: order.order_kg || 0,
            order_pcs: order.order_pcs || 0,
            packed_wt_kg: order.packed_wt_kg || 0,
            packed_pcs: order.packed_pcs || 0,
          });
        } else {
          order.details.forEach((detail, detailIndex) => {
            flattenedData.push({
              key: `${orderIndex}-${detailIndex}`,
              work_order_no: order.work_order_no,
              section: detail.section || '',
              length_mm: detail.length_mm || '',
              item_name: detail.item_name || '',
              order_kg: detail.order_kg || 0,
              order_pcs: detail.order_pcs || 0,
              packed_wt_kg: detail.packed_wt_kg || 0,
              packed_pcs: detail.packed_pcs || 0,
            });
          });
        }
      });

      return preprocessForMerge(flattenedData);
    };
    const pdfSummaryColumns = [
      { header: 'Work Order No.', dataKey: 'work_order_no' },
      { header: 'Profile No', dataKey: 'section' },
      { header: 'Length(mm)', dataKey: 'length_mm' },
      { header: 'Profile Description', dataKey: 'item_name' },
      { header: 'Order (Kg)', dataKey: 'order_kg' },
      { header: 'Order (Pcs)', dataKey: 'order_pcs' },
      { header: 'Packed Wt (Kg)', dataKey: 'packed_wt_kg' },
      { header: 'Packed (Pcs)', dataKey: 'packed_pcs' },
    ];
    const Summaryrows = transformSummaryData(dataPartyWiseSummary || []);
    const pdffilteredColumns = [
      { header: 'Work Order No.', dataKey: 'workorder_no' },
      { header: 'Bundle No', dataKey: 'bundle_no' },
      { header: 'Profile No', dataKey: 'section' },
      { header: 'Length(mm)', dataKey: 'length_mm' },
      { header: 'Profile Description', dataKey: 'item_name' },
      { header: 'Pieces', dataKey: 'pieces' },
      { header: 'Weight(kg)', dataKey: 'weight_kg' },
      { header: 'Avg Wt(kg)', dataKey: 'avg_weight_kg' },
      { header: 'Packing Date', dataKey: 'packing_date' },
    ];
    const rows = (dataTwo || []).map(item => ({
      workorder_no: item?.workorder_no || 'N/A',
      bundle_no: item?.bundle_no || 'N/A',
      section: item?.section || 'N/A',
      length_mm: item?.length_mm || 'N/A',
      item_name: item?.item_name || 'N/A',
      pieces: item?.pieces || 'N/A',
      weight_kg: item?.weight_kg || 'N/A',
      avg_weight_kg: item?.avg_weight_kg?.toFixed(3) || 'N/A',
      packing_date:
        dayjs(item?.packing_date).format('DD-MM-YYYY hh:mm') || 'N/A',
    }));
    pdf.text(
      `${dataPartyWiseSummary[0]?.customer_name || 'Customer'}`,
      pdf.internal.pageSize.width / 2,
      40,
      { align: 'center' },
    );
    pdf.text('Summary', pdf.internal.pageSize.width / 2, 60, { align: 'center' });
    pdf.autoTable({
      columns: pdfSummaryColumns,
      body: Summaryrows,
      startY: 70,
      theme: 'grid',
      styles: {
        font: 'WorkSans',
        halign: 'center',
        overflow: 'linebreak',
        cellPadding: 2,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },
      headStyles: {
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        fillColor: false,
      },
      didParseCell: function (data) {
        if (data.section === 'head') return;

        const rowIndex = data.row.index;
        if (data.column.dataKey === 'work_order_no') {
          if (mergeCellTracker.workOrderGroups[rowIndex]) {
            data.cell.rowSpan =
              mergeCellTracker.workOrderGroups[rowIndex].rowSpan;
            data.cell.text = [
              mergeCellTracker.workOrderGroups[rowIndex].workOrderNo,
            ];
            data.cell.styles.halign = 'center';
            data.cell.styles.valign = 'middle';
          } else {
            data.cell.text = [];
          }
        }
        if (data.column.dataKey === 'section') {
          if (mergeCellTracker.sectionGroups[rowIndex]) {
            data.cell.rowSpan =
              mergeCellTracker.sectionGroups[rowIndex].rowSpan;
            data.cell.text = [mergeCellTracker.sectionGroups[rowIndex].section];
            data.cell.styles.halign = 'center';
            data.cell.styles.valign = 'middle';
          } else {
            data.cell.text = [];
          }
        }
      },
    });
    let finalY = pdf.lastAutoTable.finalY + 20;
    pdf.text(
      'Packed Bundle Detail',
      pdf.internal.pageSize.width / 2,
      finalY + 10,
      {
        align: 'center',
      },
    );
    pdf.autoTable({
      columns: pdffilteredColumns,
      body: rows,
      startY: finalY + 30,
      theme: 'grid',
      styles: {
        font: 'WorkSans',
        halign: 'center',
        overflow: 'linebreak',
        cellPadding: 2,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },
      headStyles: {
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        fillColor: false,
      },
      rowStyles: {
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      },
      tableLineColor: [0, 0, 0],
      rowPageBreak: 'avoid',
      didDrawPage: data => {
        const pageCount = pdf.internal.getNumberOfPages();
        pdf.text(
          `Page ${pageCount}`,
          pdf.internal.pageSize.width - 60,
          pdf.internal.pageSize.height - 20,
        );
      },
    });
    try {
      pdf.autoPrint();
      const pdfBlob = pdf.output('blob');
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0px';
      iframe.style.bottom = '0px';
      iframe.style.width = '1px';
      iframe.style.height = '1px';
      iframe.style.border = 'none';
      iframe.src = URL.createObjectURL(pdfBlob);
      document.body.appendChild(iframe);
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          console.error('Print failed:', e);
          window.open(URL.createObjectURL(pdfBlob), '_blank');
        }
      };
    } catch (e) {
      console.error('PDF generation error:', e);
      alert('PDF generation failed. Please try again.');
    }
  };

  const handleExportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Summary Sheet
    const summaryHeaders = [
      'Work Order No',
      'Profile No',
      'Length (mm)',
      'Profile Description',
      'Order (Kg)',
      'Order (Pcs)',
      'Packed Wt (Kg)',
      'Packed (Pcs)',
    ];
    const summaryRows = transformData(dataPartyWiseSummary || []).map(item => ({
      work_order_no: item.work_order_no || 'N/A',
      section: item.section || 'N/A',
      length_mm: item.length_mm || 'N/A',
      item_name: item.item_name || 'N/A',
      order_kg: item.order_kg || 0,
      order_pcs: item.order_pcs || 0,
      packed_wt_kg: item.packed_wt_kg || 0,
      packed_pcs: item.packed_pcs || 0,
    }));
    const summaryTotal = calculateTotalRow(summaryRows);
    const summaryDataForExcel = [
      ...summaryRows,
      {
        work_order_no: '',
        section: '',
        length_mm: '',
        item_name: 'Total',
        order_kg: summaryTotal.order_kg,
        order_pcs: summaryTotal.order_pcs,
        packed_wt_kg: summaryTotal.packed_wt_kg,
        packed_pcs: summaryTotal.packed_pcs,
      },
    ];
    const summaryWs = XLSX.utils.json_to_sheet(summaryDataForExcel); // Remove header option
    XLSX.utils.sheet_add_aoa(summaryWs, [summaryHeaders], { origin: 'A1' }); // Manually add headers
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

    // Detailed Sheet
    const detailHeaders = [
      'Bundle No',
      'Profile No',
      'Length (mm)',
      'Profile Description',
      'Pieces',
      'Weight (kg)',
      'Avg Wt (kg)',
      'Packing Date',
    ];
    const detailRows = (dataTwo || []).map(item => ({
      bundle_no: item.bundle_no || 'N/A',
      section: item.section || 'N/A',
      length_mm: item.length_mm || 'N/A',
      item_name: item.item_name || 'N/A',
      pieces: item.pieces || 0,
      weight_kg: item.weight_kg || 0,
      avg_weight_kg: item.avg_weight_kg?.toFixed(3) || '0.000',
      packing_date: dayjs(item.packing_date).format('DD-MM-YYYY hh:mm') || 'N/A',
    }));
    // Calculate totals for detailRows
    let totalPieces = 0, totalWeight = 0, totalAvgWeight = 0, avgWeightCount = 0;
    detailRows.forEach(row => {
      if (typeof row.pieces === 'number') totalPieces += row.pieces;
      else if (!isNaN(Number(row.pieces))) totalPieces += Number(row.pieces);
      if (typeof row.weight_kg === 'number') totalWeight += row.weight_kg;
      else if (!isNaN(Number(row.weight_kg))) totalWeight += Number(row.weight_kg);
      if (!isNaN(Number(row.avg_weight_kg))) {
        totalAvgWeight += Number(row.avg_weight_kg);
        avgWeightCount++;
      }
    });
   
    // Add total row
    detailRows.push({
      bundle_no: '',
      section: '',
      length_mm: '',
      item_name: 'Total',
      pieces: totalPieces,
      weight_kg: totalWeight?.toFixed(3),
      packing_date: '',
    });
    const detailWs = XLSX.utils.json_to_sheet(detailRows); // Remove header option
    XLSX.utils.sheet_add_aoa(detailWs, [detailHeaders], { origin: 'A1' }); // Manually add headers
    XLSX.utils.book_append_sheet(wb, detailWs, 'Packed Bundle Detail');

    // Save the Excel file
    XLSX.writeFile(wb, `CurrentStock_${dataPartyWiseSummary[0]?.customer_name}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };
  

  const allCsvColumns = [
    { header: 'Sr. No.', dataKey: 'srNo', field: 'sr_no' },
    { header: 'Customer Name', dataKey: 'customerName', field: 'customer_name' },
    {
      header: 'Factory Address',
      dataKey: 'factoryAddress',
      field: 'factory_address',
    },
    {
      header: 'Office Address',
      dataKey: 'officeAddress',
      field: 'office_address',
    },
    { header: 'Phone Number', dataKey: 'phoneNumber', field: 'phone_number' },
    { header: 'GSTIN Number', dataKey: 'gstinNumber', field: 'gstin_number' },
    { header: 'PAN Number', dataKey: 'panNumber', field: 'pan_number' },
    { header: 'Udyam Number', dataKey: 'udyamNumber', field: 'udyam_no' },
    { header: 'GST Type', dataKey: 'gstType', field: 'gst_type' },
    { header: 'Created At', dataKey: 'createdAt', field: 'created_at' },
    { header: 'Created By', dataKey: 'createdBy', field: 'created_by' },
    { header: 'Updated At', dataKey: 'updatedAt', field: 'updated_at' },
    { header: 'Updated By', dataKey: 'updatedBy', field: 'updated_by' },
    { header: 'Customer Type', dataKey: 'customerType', field: 'customer_type' },
    { header: 'Delivery Days', dataKey: 'deliveryDays', field: 'delivery_days' },
    {
      header: 'Applicable GST',
      dataKey: 'applicableGst',
      field: 'applicable_gst',
    },
    {
      header: 'Customer Profile No.',
      dataKey: 'customerSectionNo',
      field: 'customer_section_no',
    },
    { header: 'Bank Name', dataKey: 'bankName', field: 'bank_name' },
    {
      header: 'Bank Account Number',
      dataKey: 'bankAccountNumber',
      field: 'bank_account_number',
    },
    {
      header: 'Bank IFSC Code',
      dataKey: 'bankIfscCode',
      field: 'bank_ifsc_code',
    },
    {
      header: 'Bank Branch Address',
      dataKey: 'bankBranchAddress',
      field: 'bank_branch_address',
    },
    { header: 'Email', dataKey: 'email', field: 'email' },
  ];
  const csvHeaders = allCsvColumns
    .filter(col => checkedList.includes(col.field))
    .map(col => ({
      label: col.header,
      key: col.dataKey,
    }));
  const csvListData = dataSource.map((item, index) => {
    const row = {
      srNo: index + 1,
    };
    allCsvColumns
      .filter(col => checkedList.includes(col.field))
      .forEach(col => {
        switch (col.field) {
          case 'customer_name':
            row.customerName = item?.customer_name || 'N/A';
            break;
          case 'factory_address':
            row.factoryAddress = item?.factory_address || 'N/A';
            break;
          case 'office_address':
            row.officeAddress = item?.office_address || 'N/A';
            break;
          case 'phone_number':
            row.phoneNumber = item?.phone_number || 'N/A';
            break;
          case 'gstin_number':
            row.gstinNumber = item?.gstin_number || 'N/A';
            break;
          case 'pan_number':
            row.panNumber = item?.pan_number || 'N/A';
            break;
          case 'udyam_no':
            row.udyamNumber = item?.udyam_no || 'N/A';
            break;
          case 'gst_type':
            row.gstType = item?.gst_type || 'N/A';
            break;
          case 'created_at':
            row.createdAt = item.created_at
              ? moment(item.created_at).format('DD-MM-YYYY')
              : 'N/A';
            break;
          case 'created_by':
            row.createdBy = item.created_by
              ? `${item.created_by.first_name} ${item.created_by.last_name}`
              : 'N/A';
            break;
          case 'updated_at':
            row.updatedAt = item.updated_at
              ? moment(item.updated_at).format('DD-MM-YYYY')
              : 'N/A';
            break;
          case 'updated_by':
            row.updatedBy = item.created_by
              ? `${item.created_by.first_name} ${item.created_by.last_name}`
              : 'N/A';
            break;
          case 'customer_type':
            row.customerType = item?.customer_type?.name || 'N/A';
            break;
          case 'delivery_days':
            row.deliveryDays = item?.delivery_days || 'N/A';
            break;
          case 'applicable_gst':
            row.applicableGst = item?.applicable_gst || 'N/A';
            break;
          case 'customer_section_no':
            row.customerSectionNo = item?.customer_section_no || 'N/A';
            break;
          case 'bank_name':
            row.bankName = item?.bank_name || 'N/A';
            break;
          case 'bank_account_number':
            row.bankAccountNumber = item?.bank_account_number || 'N/A';
            break;
          case 'bank_ifsc_code':
            row.bankIfscCode = item?.bank_ifsc_code || 'N/A';
            break;
          case 'bank_branch_address':
            row.bankBranchAddress = item?.bank_branch_address || 'N/A';
            break;
          case 'email':
            row.email = item?.email || 'N/A';
            break;
          default:
            row[col.dataKey] = 'N/A';
        }
      });
    return row;
  });

  return (
    <div>
      <div className="sticyaction">
        <div className="sticky-header">
          <Row
            className="pagenamerow mb-0 "
            justify="space-between"
            align="middle"
          >
            <Col>
              <h2 style={{ margin: 0 }}>Current Stock</h2>
              <div className="bredcrumbwrp">
                <Link to="/current-stock-data" className="back">
                  <Back /> BACK
                </Link>
                <Breadcrumb
                  items={[
                    { title: <Link to="/dashboard">Home</Link> },
                    {
                      title: (
                        <Link to="/current-stock-data">
                          {' '}
                          Current Stock Data
                        </Link>
                      ),
                    },
                    { title: 'Current Stock' },
                  ]}
                />
              </div>
            </Col>
            <Col>
              <Space size="small">
                <Tooltip title="Print">
                  <Button
                    type="primary"
                    className="iconlink"
                    onClick={handlePrintDownload}
                  >
                    <PrintIcon />
                  </Button>
                </Tooltip>
                <Tooltip title="Export to Excel">
                  <Button
                    type="primary"
                    className="iconlink"
                    onClick={handleExportToExcel}
                  >
                    <FileExcelOutlined />
                  </Button>
                </Tooltip>
              </Space>
            </Col>
          </Row>
        </div>
      </div>
    
      <div
        style={{
          textAlign: 'center',
          margin: '15px',
        }}
      >
        <h1>{dataPartyWiseSummary[0]?.customer_name}</h1>
        <h3> Summary </h3>
      </div>
      <Table
        rowKey="key"
        columns={summaryColumns}
        dataSource={transformedData}
        pagination={false}
        bordered
        summary={pageData => {
          let totalOrderKg = 0,
            totalOrderPcs = 0,
            totalPackedWt = 0,
            totalPackedPcs = 0;

          pageData.forEach(
            ({ order_kg, order_pcs, packed_wt_kg, packed_pcs }) => {
              totalOrderKg += order_kg;
              totalOrderPcs += order_pcs;
              totalPackedWt += packed_wt_kg;
              totalPackedPcs += packed_pcs;
            },
          );

          return (
            <Table.Summary.Row>
              <Table.Summary.Cell
                index={0}
                colSpan={5}
                style={{ fontWeight: 'bold', textAlign: 'center' }}
              >
                <b>TOTAL</b>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} style={{ fontWeight: 'bold' }}>
                <b> {totalOrderKg?.toFixed(3)}</b>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} style={{ fontWeight: 'bold' }}>
                <b> {totalOrderPcs}</b>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} style={{ fontWeight: 'bold' }}>
                <b> {totalPackedWt?.toFixed(3)}</b>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8} style={{ fontWeight: 'bold' }}>
                <b>{totalPackedPcs}</b>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />
      <div
        style={{
          textAlign: 'center',
          margin: '15px',
        }}
      >
        <h2> Packed Bundle Detail</h2>
      </div>

      <Table
        rowKey="key"
        columns={filteredColumns}
        dataSource={dataSourceTwo}
        pagination={false}
        pageSizeOptions={["20", "50", "100"]}
        loading={status === 'loading'}
        scroll={{ x: 180 }}
        summary={pageData => {
          let totalPieces = 0,
            totalWeight = 0,
            totalAvgWeight = 0,
            avgWeightCount = 0;

          pageData.forEach(row => {
            if (typeof row.pieces === 'number') totalPieces += row.pieces;
            if (typeof row.weight_kg === 'number') totalWeight += row.weight_kg;
            if (typeof row.avg_weight_kg === 'number') {
              totalAvgWeight += row.avg_weight_kg;
              avgWeightCount++;
            }
          });

          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5} style={{ fontWeight: 'bold', textAlign: 'center' }}>
                <b>TOTAL ({pageData?.length} Bundles)</b>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} style={{ fontWeight: 'bold' }}>
                <b>{totalPieces}</b>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} style={{ fontWeight: 'bold' }}>
                <b>{totalWeight?.toFixed(3)}</b>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />
    </div>
  );
}

export default CurrentStockList;